/**
 * 軽量テキスト類似度計算 (Sprint X5f / R-38 #2、旧 `old/apps/user/src/components/ecw/textAnalysis.ts` 100 行を踏襲)。
 *
 * 外部 API / 重い辞書ライブラリに依存せず、`Intl.Segmenter` (モダンブラウザ標準) で日本語単語分割し、
 * stop word filter → TF ベクトル → cosine 類似度を返す。
 *
 * Sprint BF Part 3 (GAP-U9) — 旧 100 行を精度面で改善:
 *   - `Intl.Segmenter` 非対応環境 (node test 環境の一部 / 古い browser) では **文字 bigram fallback** を使用、
 *     全環境で類似度が計算可能になる (旧は null fallback で常に 0 を返していた)
 *   - **IDF 重み (`buildIdf` + `calculateTextSimilarityWithIdf`)** を追加し、希少語ほど類似度に効くようにする。
 *     呼出側で post.title + observations の corpus から IDF を 1 度算出 → 各 pair で同じ IDF を使い回す pattern。
 *   - hiragana / katakana / kanji boundary を考慮した bigram (記号 / 空白で区切ってから 2-gram 生成、純粋な
 *     N-gram よりノイズが少ない、外部 dep 不要で ~150 行内)
 *
 * 旧との差分 (X5f → 本 sprint):
 *   - `@ts-ignore` を除去 (TS lib.es2022.intl で Intl.Segmenter 型が存在するため、unsupported 環境のみ fallback)
 *   - export 名 (`tokenizeText` / `getTermFrequencies` / `cosineSimilarity` / `calculateTextSimilarity`) は維持
 *   - `tokenizeText` は Segmenter 非対応時に bigram を返すため、結果配列は呼出側で同様に扱える (TF ベクトル化に互換)
 *   - 新規 export: `tokenizeFallback` (bigram、test 用 + 明示利用)、`buildIdf` / `calculateTextSimilarityWithIdf`
 *
 * 用途:
 *   - AnalysisNetworkGraphView: 投稿間 implicit 類似度 link 計算 (threshold > 0.15 で top N pair)
 *   - 将来: insight/idea 間の関連検出など
 */

const isSegmenterSupported =
  typeof Intl !== 'undefined' && typeof (Intl as { Segmenter?: unknown }).Segmenter === 'function';

const segmenter: Intl.Segmenter | null = isSegmenterSupported
  ? new Intl.Segmenter('ja-JP', { granularity: 'word' })
  : null;

const stopWordRegex =
  /^[ぁ-ん]$|^[、。！？\s.,!?・()「」『』【】（）]$|^ある$|^いる$|^する$|^できる$|^しまう$|^など$|^ため$|^こと$|^もの$|^これ$|^それ$|^あれ$|^どれ$|^この$|^その$|^あの$|^どの$/;

/** 区切り文字 (記号 / 空白 / 句読点)。bigram 生成前にここで split。 */
const TOKEN_SPLIT_REGEX = /[\s.,!?、。！？・()「」『』【】（）\-_/:;]+/u;

/**
 * 文字種 (hiragana / katakana / kanji / latin / other) を判定して、文字種境界で再分割する helper。
 * 旧 v1 textAnalysis の Segmenter fallback として「軽量形態素相当」の品質を出すため、
 * 連続する同種文字を 1 つの「セグメント」として扱い、その上で 2-gram を取る。
 *
 * 例: "テスト123あいう" → ["テスト", "123", "あいう"] → 各セグメントで bigram。
 */
function classifyChar(ch: string): 'hira' | 'kata' | 'kanji' | 'latin' | 'other' {
  const code = ch.charCodeAt(0);
  if (code >= 0x3040 && code <= 0x309f) return 'hira';
  if (code >= 0x30a0 && code <= 0x30ff) return 'kata';
  if (code >= 0x4e00 && code <= 0x9fff) return 'kanji';
  if ((code >= 0x30 && code <= 0x39) || (code >= 0x41 && code <= 0x7a)) return 'latin';
  return 'other';
}

function splitByCharClass(seg: string): string[] {
  if (seg.length === 0) return [];
  const out: string[] = [];
  let cur = seg[0] ?? '';
  let curClass = classifyChar(cur);
  for (let i = 1; i < seg.length; i++) {
    const ch = seg[i] ?? '';
    const cls = classifyChar(ch);
    if (cls === curClass) {
      cur += ch;
    } else {
      out.push(cur);
      cur = ch;
      curClass = cls;
    }
  }
  if (cur.length > 0) out.push(cur);
  return out;
}

/**
 * Intl.Segmenter 非対応環境用の **fallback トークナイザ** (Sprint BF Part 3、GAP-U9)。
 *
 * 1. TOKEN_SPLIT_REGEX で句読点 / 空白 / 記号で大まかに切る
 * 2. 各セグメントを文字種境界 (hira/kata/kanji/latin) で再分割
 * 3. 長さ 2 以上は文字 2-gram、長さ 1 はそのまま採用
 * 4. ASCII (latin) セグメントは下記の通り単語単位 (空白で既に split 済) としてそのまま
 *
 * 旧 v1 では Segmenter 非対応時に空配列を返していたが、これだと全 pair 0 になるので
 * node test / SSR 等で類似度比較が役に立たなくなる。本 bigram fallback で最低限の精度を確保する。
 */
export function tokenizeFallback(text: string): string[] {
  if (!text) return [];
  const tokens: string[] = [];
  const rough = text.split(TOKEN_SPLIT_REGEX);
  for (const part of rough) {
    if (!part) continue;
    const subSegs = splitByCharClass(part);
    for (const seg of subSegs) {
      if (seg.length === 0) continue;
      if (stopWordRegex.test(seg)) continue;
      // latin (英数) は語単位として extra split せずそのまま採用 (短いトークンは bigram 化しない)
      const cls = classifyChar(seg[0] ?? '');
      if (cls === 'latin') {
        tokens.push(seg.toLowerCase());
        continue;
      }
      if (seg.length === 1) {
        // 1 文字 hira は stop word 含む、kanji / kata 1 文字は採用
        if (cls !== 'hira') tokens.push(seg);
        continue;
      }
      // 2-gram 生成
      for (let i = 0; i < seg.length - 1; i++) {
        const bi = seg.slice(i, i + 2);
        if (!stopWordRegex.test(bi)) tokens.push(bi);
      }
    }
  }
  return tokens;
}

export function tokenizeText(text: string): string[] {
  if (!text) return [];
  if (!segmenter) return tokenizeFallback(text);
  const tokens: string[] = [];
  const segments = segmenter.segment(text);
  for (const segment of segments) {
    if (segment.isWordLike) {
      const word = segment.segment.trim();
      if (word.length > 0 && !stopWordRegex.test(word)) {
        tokens.push(word);
      }
    }
  }
  // Segmenter が無語と判定したケース (短文・記号混じり等) は fallback を試行
  if (tokens.length === 0) return tokenizeFallback(text);
  return tokens;
}

export function getTermFrequencies(tokens: string[]): Record<string, number> {
  const tf: Record<string, number> = {};
  for (const token of tokens) {
    tf[token] = (tf[token] || 0) + 1;
  }
  return tf;
}

export function cosineSimilarity(tf1: Record<string, number>, tf2: Record<string, number>): number {
  let dotProduct = 0;
  let norm1 = 0;
  let norm2 = 0;
  const allKeys = new Set([...Object.keys(tf1), ...Object.keys(tf2)]);
  for (const key of allKeys) {
    const val1 = tf1[key] || 0;
    const val2 = tf2[key] || 0;
    dotProduct += val1 * val2;
    norm1 += val1 * val1;
    norm2 += val2 * val2;
  }
  if (norm1 === 0 || norm2 === 0) return 0;
  return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
}

export function calculateTextSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;
  const tokens1 = tokenizeText(text1);
  const tokens2 = tokenizeText(text2);
  if (tokens1.length === 0 || tokens2.length === 0) return 0;
  const tf1 = getTermFrequencies(tokens1);
  const tf2 = getTermFrequencies(tokens2);
  return cosineSimilarity(tf1, tf2);
}

/**
 * IDF (Inverse Document Frequency) map を corpus から構築 (Sprint BF Part 3、GAP-U9)。
 *
 * 用途: corpus = 全 post の title + observations 等。希少語ほど高い weight を持つようにし、
 * cosine 類似度の精度を底上げする (旧は TF のみ、共通の助詞・サフィックスが過剰に類似と判定された)。
 *
 * 戻り値は `{ [term]: log((N + 1) / (df + 1)) }`。N = corpus 件数、df = term を含む doc 数。
 */
export function buildIdf(texts: string[]): Record<string, number> {
  const N = texts.length;
  const df: Record<string, number> = {};
  for (const t of texts) {
    const tokens = new Set(tokenizeText(t));
    for (const tok of tokens) {
      df[tok] = (df[tok] || 0) + 1;
    }
  }
  const idf: Record<string, number> = {};
  for (const tok of Object.keys(df)) {
    const dfVal = df[tok] ?? 0;
    idf[tok] = Math.log((N + 1) / (dfVal + 1)) + 1; // +1 smoothing
  }
  return idf;
}

/** TF ベクトルに IDF を掛けた tf-idf ベクトルを返す。 */
function applyIdf(tf: Record<string, number>, idf: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of Object.keys(tf)) {
    const tfVal = tf[k] ?? 0;
    const w = idf[k] ?? 1; // corpus 外の語は weight 1
    out[k] = tfVal * w;
  }
  return out;
}

/**
 * IDF 重み付き類似度 (Sprint BF Part 3、GAP-U9)。
 *
 * 呼出側で `buildIdf(corpus)` を 1 度だけ実行し、各 pair で再利用すると O(N^2) でも軽量に
 * tf-idf cosine 類似度が出せる。AnalysisNetworkGraphView の implicit link 計算でも使用予定。
 */
export function calculateTextSimilarityWithIdf(
  text1: string,
  text2: string,
  idf: Record<string, number>,
): number {
  if (!text1 || !text2) return 0;
  const tokens1 = tokenizeText(text1);
  const tokens2 = tokenizeText(text2);
  if (tokens1.length === 0 || tokens2.length === 0) return 0;
  const tf1 = applyIdf(getTermFrequencies(tokens1), idf);
  const tf2 = applyIdf(getTermFrequencies(tokens2), idf);
  return cosineSimilarity(tf1, tf2);
}
